/**
 * @file useAppStore.js
 * @location src/store/useAppStore.js
 * @role 전역 상태 관리 (useReducer 기반) — 초기 state + 영속화 + 액션 디스패처
 *
 * 실제 액션 핸들러는 도메인별로 src/store/reducers/ 에 분리되어 있다 (reducers/index.js 가 병합):
 *   navActions         네비/필터/사용자 전환
 *   taskActions        업무 CRUD + 핸드셰이크 (세부는 taskActions/ 서브트리: crudActions·handshakeActions·crud/lifecycle·crud/trash)
 *   templateActions    반복 업무 템플릿 CRUD (ADD/EDIT/TOGGLE/DELETE_TEMPLATE)
 *   autoActions        자동 처리 (AUTO_CARRY_OVER · AUTO_GENERATE_RECURRING · AUTO_PURGE_TRASH)
 *   memberActions      인원 관리
 *   uiActions          다크 모드/모달 패치/초기화/온보딩
 *   syncActions        Supabase realtime 수신 반영 (SYNC_* / HYDRATE)
 *   outboxActions      오프라인 outbox 큐 (ENQUEUE/REMOVE/DROP_STALE)
 *
 * 영속화: 영속 필드(tasks/deletedTasks/taskTemplates/darkMode/members/companyName/uiPrefs/onboarded/outbox)
 *         → localStorage 키 'workdo_v2'.  마이그레이션 본체는 src/utils/migrations.js
 *   (일보 ⑱·부서 ⑲·직급 ⑳·ToDo·Timeline ㉑ 폐기)
 */
import { useReducer, useCallback, useEffect } from 'react';
import {
  INIT_TASKS,
  INIT_MEMBERS,
  saveToStorage, loadFromStorage,
} from '../data/constants';
import { handlers } from './reducers';

// ── 초기 state ──
export const getInitialState = (saved) => {
  if (saved == null) saved = loadFromStorage();
  // 빈 배열([]) 방어: `?? INIT_MEMBERS` 는 null/undefined 만 막아 빈 배열을 통과시킨다.
  // members:[] 가 한번 영속되면 members[0].id 가 크래시 → 로그인 화면조차 못 뜨고 앱 전체가 잠김.
  const members = saved?.members?.length ? saved.members : INIT_MEMBERS;
  return {
    uid:          members[0].id,
    members,
    tasks:         saved?.tasks          ?? INIT_TASKS,
    deletedTasks:  saved?.deletedTasks   ?? [],
    taskTemplates: saved?.taskTemplates  ?? [],
    companyName:   saved?.companyName    ?? '',
    uiPrefs:       saved?.uiPrefs        ?? { sectionOpen: {} },
    // 저장 없음 = 신규 워크스페이스 → 온보딩 위저드 표시. 기존 사용자는 migrate 가 true 백필
    onboarded:     saved?.onboarded      ?? false,
    darkMode:      saved?.darkMode       ?? false,
    // ── 오프라인 outbox 큐 ── saved 에 있으면 복원(reload 시 큐 손실 방지)
    outbox:        Array.isArray(saved?.outbox) ? saved.outbox : [],
    // ── 네비게이션 ──
    // 초기 진입 화면은 전원 업무(마이) — 대시보드 폐지, 핵심 루프로 진입점 일원화
    activeNav:    'tasks',
    // ── 모달 (전역 필요: 여러 컴포넌트에서 열고 닫음) ──
    modalTask:        false,
    modalProg:        false,
    // 초대 발급 모달 — 인원 관리 ＋멤버초대 진입
    modalInviteMember: false,
    modalShortcuts:   false,
    // 설정 모달 — 아바타/⋯ 메뉴(매니저 게이트)에서 열림. 워크스페이스 운영(구독·데이터·초기화)
    modalSettings:    false,
    // TaskModal 진입 시 Step 1 사전 선택 담당- id — SET_UI 로 세팅 (transient)
    taskModalAee:     null,
    // 루틴업무 모달(RoutineModal) — id(null=신규) + 초기 모드(read=상세/edit=편집)
    modalRoutine:     false,
    routineModalId:   null,
    routineModalMode: 'edit',
    progTargetId:     null,
  };
};

// ── Reducer 디스패처: 도메인별 handlers 맵에서 위임 ──
function reducer(state, action) {
  const handler = handlers[action.type];
  return handler ? handler(state, action) : state;
}

// ── Hook ──
export function useAppStore() {
  const [S, dispatch] = useReducer(reducer, null, getInitialState);

  useEffect(() => {
    document.body.classList.toggle('dark', S.darkMode);
  }, [S.darkMode]);

  useEffect(() => {
    saveToStorage({
      tasks: S.tasks, deletedTasks: S.deletedTasks,
      taskTemplates: S.taskTemplates,
      darkMode: S.darkMode,
      members: S.members,
      companyName: S.companyName,
      uiPrefs: S.uiPrefs,
      onboarded: S.onboarded,
      outbox: S.outbox,
    });
  }, [S.tasks, S.deletedTasks, S.taskTemplates, S.darkMode, S.members, S.companyName, S.uiPrefs, S.onboarded, S.outbox]);

  const act = useCallback((type, patch = {}) => dispatch({ type, ...patch }), []);

  return { S, dispatch, act };
}
